import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
  additionalSection2?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: 'Designing Supportive Environments',
    content: [
      '<strong>Learning Goal:</strong> Apply environmental design principles for mental health and productivity, identify the link between environmental justice and well-being, and create a personal plan to improve your surroundings sustainably.',
      '<br/>',
      '<strong>Key Topics:</strong>',
      '• Environmental psychology and space design',
      '• Creating restorative spaces',
      '• The importance of green spaces and nature',
      '• Building safe and inclusive communities',
      '• Access to essential services',
      '<br/>',
      '<strong>Discussion:</strong> Learn how to intentionally design environments – physical and social – that support well-being, sustainability, and comfort. Environmental design isn\'t just about aesthetics; it\'s about creating spaces that nurture health and happiness.'
    ]
  },
  {
    title: 'Environmental Psychology Principles',
    content: [
      'Environmental psychology studies how physical spaces affect human behavior, emotions, and well-being.',
      '<br/>',
      '<strong>Key Design Principles:</strong>',
      '• <strong>Restorative Spaces:</strong> Areas that reduce stress and restore mental energy',
      '• <strong>Prospect and Refuge:</strong> Spaces offering both safety and the ability to see surroundings',
      '• <strong>Wayfinding:</strong> Clear navigation that reduces cognitive load',
      '• <strong>Personalization:</strong> Spaces that reflect individual identity and comfort',
      '<br/>',
      '<strong>Psychological Benefits:</strong>',
      '• <strong>Stress Reduction:</strong> Well-designed spaces lower cortisol levels',
      '• <strong>Improved Focus:</strong> Organized environments enhance concentration',
      '• <strong>Mood Enhancement:</strong> Pleasant spaces boost positive emotions',
      '• <strong>Social Connection:</strong> Inclusive designs foster community',
      '<br/>',
      '<strong>The Biophilia Hypothesis:</strong> Humans have an innate tendency to seek connections with nature. Incorporating natural elements in design supports psychological well-being.',
      '<br/>',
      '<strong>Evidence-Based Design:</strong> Research shows that environmental design can improve mental health outcomes by 20-30% in clinical and workplace settings.'
    ]
  },
  {
    title: 'Creating Restorative Spaces',
    content: [
      'Restorative spaces help you recover from stress and maintain mental balance.',
      '<br/>',
      '<strong>Essential Elements:</strong>',
      '• <strong>Natural Light:</strong> Windows and skylights that connect you to the outdoors',
      '• <strong>Plant Life:</strong> Indoor plants that purify air and reduce stress',
      '• <strong>Comfortable Seating:</strong> Ergonomic furniture that supports relaxation',
      '• <strong>Personal Touches:</strong> Meaningful objects that bring joy',
      '• <strong>Sound Control:</strong> Acoustic treatments that manage noise levels',
      '<br/>',
      '<strong>Restorative Activities:</strong>',
      '• <strong>Mindful Observation:</strong> Taking time to notice and appreciate your surroundings',
      '• <strong>Nature Connection:</strong> Bringing natural elements indoors',
      '• <strong>Space Clearing:</strong> Regularly decluttering and organizing',
      '• <strong>Sensory Balance:</strong> Managing light, sound, and temperature',
      '<br/>',
      '<strong>Workplace Applications:</strong>',
      '• Designated break areas with natural views',
      '• Plants and natural materials in office spaces',
      '• Flexible workspaces that accommodate different work styles',
      '• Quiet zones for focused work and stress recovery'
    ]
  },
  {
    title: 'The Power of Green Spaces',
    content: [
      'Access to nature and green spaces has profound effects on physical and mental health.',
      '<br/>',
      '<strong>Health Benefits:</strong>',
      '• <strong>Stress Reduction:</strong> Nature exposure lowers cortisol and improves mood',
      '• <strong>Improved Cognition:</strong> Green spaces enhance attention and memory',
      '• <strong>Physical Activity:</strong> Natural areas encourage movement and exercise',
      '• <strong>Social Connection:</strong> Parks and green spaces facilitate community interaction',
      '• <strong>Immune Support:</strong> Nature exposure strengthens immune function',
      '<br/>',
      '<strong>Urban Green Solutions:</strong>',
      '• <strong>Community Gardens:</strong> Local spaces for growing food and community building',
      '• <strong>Green Roofs:</strong> Vegetation on building rooftops for insulation and air quality',
      '• <strong>Street Trees:</strong> Urban trees that provide shade and reduce heat islands',
      '• <strong>Window Gardens:</strong> Indoor and outdoor container gardening',
      '<br/>',
      '<strong>Environmental Justice:</strong> Access to green spaces often correlates with socioeconomic status. Communities with more green space typically have better health outcomes.',
      '<br/>',
      '<strong>Shinrin-yoku (Forest Bathing):</strong> The Japanese practice of spending time in nature has been shown to reduce stress hormones, improve sleep, and boost immune function.'
    ]
  },
  {
    title: 'Building Inclusive Communities',
    content: [
      'Safe, inclusive communities are essential for environmental well-being.',
      '<br/>',
      '<strong>Community Design Elements:</strong>',
      '• <strong>Walkable Neighborhoods:</strong> Safe streets that encourage walking and social interaction',
      '• <strong>Public Spaces:</strong> Parks, plazas, and gathering areas accessible to all',
      '• <strong>Mixed-Use Development:</strong> Residential, commercial, and recreational spaces in proximity',
      '• <strong>Universal Design:</strong> Spaces accessible to people of all ages and abilities',
      '<br/>',
      '<strong>Social Connection Factors:</strong>',
      '• <strong>Third Places:</strong> Neutral public spaces (cafes, libraries, parks) for social interaction',
      '• <strong>Community Programs:</strong> Events and activities that bring people together',
      '• <strong>Neighbor Networks:</strong> Systems for mutual support and resource sharing',
      '• <strong>Cultural Spaces:</strong> Areas that celebrate diverse cultural expressions',
      '<br/>',
      '<strong>Environmental Justice Considerations:</strong>',
      '• Ensuring equitable access to community resources',
      '• Addressing transportation barriers',
      '• Including diverse voices in community planning',
      '• Creating affordable housing options',
      '<br/>',
      '<strong>Community Health Impact:</strong> Strong social connections in supportive environments can reduce depression by 50% and increase life satisfaction significantly.'
    ]
  },
  {
    title: 'Restorative Space Challenge Activity',
    content: [
      'Design and implement a restorative space in your environment.',
      '<br/>',
      '<strong>Activity Steps:</strong>',
      '1. <strong>Select a Space:</strong> Choose one environment you can modify (room, desk, balcony, corner of a park)',
      '2. <strong>Assess Current State:</strong> Take a photo and note how the space currently makes you feel',
      '3. <strong>Design Improvements:</strong> Plan 3-5 changes based on restorative design principles',
      '4. <strong>Implement Changes:</strong> Make the improvements over 1-2 weeks',
      '5. <strong>Evaluate Results:</strong> Take an after photo and track mood/energy changes',
      '<br/>',
      '<strong>Design Ideas by Space Type:</strong>',
      '<br/>',
      '<strong>Home Office:</strong>',
      '• Add plants for air purification and visual calm',
      '• Position desk to maximize natural light',
      '• Include personal meaningful objects',
      '• Add acoustic panels or plants to manage noise',
      '<br/>',
      '<strong>Bedroom:</strong>',
      '• Use calming colors and natural materials',
      '• Ensure proper temperature control',
      '• Add blackout curtains for better sleep',
      '• Include aromatherapy or calming scents',
      '<br/>',
      '<strong>Outdoor Space:</strong>',
      '• Add comfortable seating',
      '• Include water features or wind chimes',
      '• Plant native flowers or herbs',
      '• Create a small meditation area',
      '<br/>',
      '<strong>Reflection:</strong> How did the changes affect your stress levels, focus, and overall well-being? What surprised you about the impact of environmental design?'
    ]
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You\'ve completed Module 4: Designing Supportive Environments! Now, let\'s reinforce your learning with a quick quiz.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your knowledge about environmental design and restorative spaces.',
        'The quiz will help you review key concepts about creating environments that support well-being.'
      ]
    }
  }
];

const EnvironmentalModule4Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('environmentalM4');
  const totalSteps = moduleContent.length;

  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      await saveProgress(currentStep, true);
      navigate('/panel/learn/environmental-resources');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/environmental-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        <div
          className="module-left environmental-panel"
        >
          <div className="module-header">
            <h1>Module 4</h1>
            <p className="module-subtitle">Designing Supportive Environments</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Learn to create environments that support well-being, sustainability, and comfort.
            </p>
          </div>
        </div>

        <div className="module-right">
          <div className="module-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title">
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection2 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection2.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection2.content.map((paragraph: string, index: number) => (
                    <p key={`additional2-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false}
            >
              {currentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>

            {showCompletionMessage ? (
              <button
                className="btn-primary"
                onClick={() => navigate('/panel/learn/environmental-module-4/quiz')}
              >
                Take the Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnvironmentalModule4Page;

